/**
 * Zustand store for the AI Recording Studio module.
 * Manages recording state, projects, settings, and UI state.
 */
import { create } from "zustand";

const API = process.env.REACT_APP_BACKEND_URL;

export const useRecordingStore = create((set, get) => ({
  // ── Projects ──
  projects: [],
  currentProject: null,
  projectsLoading: false,

  // ── Recordings ──
  recordings: [],
  recordingsLoading: false,

  // ── Stats ──
  stats: null,
  statsLoading: false,

  // ── Recording State ──
  isRecording: false,
  isPaused: false,
  recordingTime: 0,
  mediaRecorder: null,
  recordingStream: null,

  // ── Settings ──
  settings: {
    resolution: "1080p",
    fps: "30",
    bitrate: 5000,
    codec: "vp8",
    source: "screen",
    saveFormat: "webm", // "webm" | "mp4" (mp4 falls back to server-side ffmpeg)
    includeMic: true,
    includeSystemAudio: false,
    includeWebcam: false,
  },

  // ── Multi-Panel State ──
  panels: [
    { id: "panel-1", type: "terminal", zoom: 1, settings: {} },
    { id: "panel-2", type: "screen-share", zoom: 1, settings: {} },
  ],
  layoutMode: "2col", // "2col" | "3col" | "2x2" | "1+3"
  panelSizes: {}, // { "panel-1": { w: 50, h: 100 }, ... } percentages
  markers: [],
  countdown: 3,
  countdownActive: false,

  // ── Exports ──
  exports: [],
  exportsLoading: false,

  // ── Recent Scripts ──
  recentScripts: [],

  // ── UI State ──
  activeTab: "dashboard",
  sidebarCollapsed: false,

  // ── Actions ──

  setActiveTab: (tab) => set({ activeTab: tab }),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  setSettings: (newSettings) =>
    set((state) => ({ settings: { ...state.settings, ...newSettings } })),

  // ── Panel Actions ──
  setLayoutMode: (mode) => {
    const panelCounts = { "2col": 2, "3col": 3, "2x2": 4, "1+3": 4 };
    const count = panelCounts[mode] || 2;
    set((state) => {
      const current = state.panels;
      let panels;
      if (current.length >= count) {
        panels = current.slice(0, count);
      } else {
        const types = ["terminal", "screen-share", "pdf", "whiteboard", "browser", "webcam"];
        panels = [...current];
        while (panels.length < count) {
          const idx = panels.length;
          panels.push({
            id: `panel-${Date.now()}-${idx}`,
            type: types[idx % types.length],
            zoom: 1,
            settings: {},
          });
        }
      }
      return { layoutMode: mode, panels, panelSizes: {} };
    });
  },

  setPanelType: (panelId, type) =>
    set((state) => ({
      panels: state.panels.map((p) =>
        p.id === panelId ? { ...p, type, settings: {} } : p
      ),
    })),

  setPanelZoom: (panelId, zoom) =>
    set((state) => ({
      panels: state.panels.map((p) =>
        p.id === panelId ? { ...p, zoom: Math.max(0.5, Math.min(3, zoom)) } : p
      ),
    })),

  setPanelSettings: (panelId, settings) =>
    set((state) => ({
      panels: state.panels.map((p) =>
        p.id === panelId ? { ...p, settings: { ...p.settings, ...settings } } : p
      ),
    })),

  setPanelSizes: (sizes) =>
    set((state) => ({ panelSizes: { ...state.panelSizes, ...sizes } })),

  swapPanels: (id1, id2) =>
    set((state) => {
      const panels = [...state.panels];
      const i1 = panels.findIndex((p) => p.id === id1);
      const i2 = panels.findIndex((p) => p.id === id2);
      if (i1 >= 0 && i2 >= 0) [panels[i1], panels[i2]] = [panels[i2], panels[i1]];
      return { panels };
    }),

  addMarker: (label) =>
    set((state) => ({
      markers: [
        ...state.markers,
        { id: Date.now(), time: state.recordingTime, label: label || `Marqueur ${state.markers.length + 1}` },
      ],
    })),

  clearMarkers: () => set({ markers: [] }),
  setCountdown: (val) => set({ countdown: val }),
  setCountdownActive: (val) => set({ countdownActive: val }),

  // ── Project Actions ──

  fetchProjects: async (token, params = {}) => {
    set({ projectsLoading: true });
    try {
      const query = new URLSearchParams();
      if (params.status) query.set("status", params.status);
      if (params.favorite !== undefined)
        query.set("favorite", params.favorite);
      if (params.search) query.set("search", params.search);

      const res = await fetch(`${API}/api/studio/projects?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch projects");
      const data = await res.json();
      set({ projects: data, projectsLoading: false });
      return data;
    } catch (err) {
      set({ projectsLoading: false });
      throw err;
    }
  },

  createProject: async (token, projectData) => {
    const res = await fetch(`${API}/api/studio/projects`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projectData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to create project");
    }
    const project = await res.json();
    set((state) => ({ projects: [project, ...state.projects] }));
    return project;
  },

  updateProject: async (token, projectId, updates) => {
    const res = await fetch(`${API}/api/studio/projects/${projectId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error("Failed to update project");
    const updated = await res.json();
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? updated : p)),
      currentProject:
        state.currentProject?.id === projectId
          ? updated
          : state.currentProject,
    }));
    return updated;
  },

  deleteProject: async (token, projectId) => {
    const res = await fetch(`${API}/api/studio/projects/${projectId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to delete project");
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== projectId),
    }));
  },

  // ── Recording Session Actions ──

  registerRecording: async (token, projectId, recordingData) => {
    const res = await fetch(
      `${API}/api/studio/projects/${projectId}/recordings`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(recordingData),
      }
    );
    if (!res.ok) throw new Error("Failed to register recording");
    return await res.json();
  },

  fetchRecordings: async (token, projectId) => {
    set({ recordingsLoading: true });
    try {
      const res = await fetch(
        `${API}/api/studio/projects/${projectId}/recordings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch recordings");
      const data = await res.json();
      set({ recordings: data, recordingsLoading: false });
      return data;
    } catch (err) {
      set({ recordingsLoading: false });
      throw err;
    }
  },

  // ── Stats ──

  fetchStats: async (token) => {
    set({ statsLoading: true });
    try {
      const res = await fetch(`${API}/api/studio/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch stats");
      const data = await res.json();
      set({ stats: data, statsLoading: false });
      return data;
    } catch (err) {
      set({ statsLoading: false });
      throw err;
    }
  },

  // ── Exports ──

  fetchExports: async (token, projectId) => {
    set({ exportsLoading: true });
    try {
      const query = projectId ? `?project_id=${projectId}` : "";
      const res = await fetch(`${API}/api/studio/exports${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch exports");
      const data = await res.json();
      set({ exports: data, exportsLoading: false });
      return data;
    } catch (err) {
      set({ exportsLoading: false });
      throw err;
    }
  },

  createExport: async (token, exportData) => {
    const res = await fetch(`${API}/api/studio/exports`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(exportData),
    });
    if (!res.ok) throw new Error("Failed to create export");
    const exp = await res.json();
    set((state) => ({ exports: [exp, ...state.exports] }));
    return exp;
  },

  // ── Recent Scripts ──

  fetchRecentScripts: async (token) => {
    try {
      const res = await fetch(`${API}/api/studio/recent-scripts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        set({ recentScripts: data });
      }
    } catch {
      // Non-critical — fail silently
    }
  },

  // ── Recording Control ──

  setRecordingState: (state) => set(state),

  resetRecordingState: () =>
    set({
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      mediaRecorder: null,
      recordingStream: null,
    }),
}));
